import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import Select from 'react-select';
import { SERVER_BASE } from '../config/server';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from 'sweetalert2';

const DepartmentItem = ({ accessOfDepartments }) => {
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const code = localStorage.getItem('code');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (accessOfDepartments) {
      const deptArray = accessOfDepartments
        .split(',')
        .map((dept) => ({ value: dept.trim(), label: dept.trim() }));
      setDepartments(deptArray);
    }
  }, [accessOfDepartments]);

  const handleGetItems = async () => {
    if (!selectedDepartment) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Pleaser Select department!',
        position: 'top-end',  // Positioning Swal to the right end
        showConfirmButton: false,
        timer: 1500, // Auto close after 1.5 seconds
        toast: true,
      });
      setItems([])
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get(SERVER_BASE + "/getItemsByDept", {
        params: { centerCode: code, deptName: selectedDepartment.value },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = () => {
    if (!selectedDepartment) {
      alert('Please select a department to export data');
      return;
    }

    // Add serial numbers and filter visible columns
    const exportData = items.map((item, index) => ({
      'S. No': index + 1,
      ...columns.reduce((acc, col) => {
        if (col.accessor && col.accessor !== 'serialNumber') {
          acc[col.Header] = item[col.accessor];
        }
        return acc;
      }, {}),
    }));

    // Add department name as header
    const worksheet = XLSX.utils.json_to_sheet(exportData, { origin: 2 });
    XLSX.utils.sheet_add_aoa(worksheet, [[`Department: ${selectedDepartment.label}`]], { origin: 0 });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Items");
    XLSX.writeFile(workbook, `${selectedDepartment.label}_Items.xlsx`);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'S. No',
        accessor: 'serialNumber',
        Cell: ({ row }) => row.index + 1, // Calculate serial number dynamically
      },
      { Header: 'Group Code', accessor: 'itemGroupCode' },
      { Header: 'Name', accessor: 'printName' },
      { Header: 'Type', accessor: 'type' },
      { Header: 'UOM', accessor: 'uom' },
      { Header: 'Avg Weight', accessor: 'avgWeight' },
      { Header: 'Additional Names', accessor: 'additionalNames' },
      { Header: 'Description', accessor: 'itemDesc' },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { pageIndex, pageSize, globalFilter },
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data: items,
      initialState: { pageIndex: 0 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  return (
    <div className="formcontainer mt-3">
      <h5>Department Item List</h5>
      <div className="mb-4">
        <div className="row align-items-end">
          <div className="col-md-4">
            <label>Select Department:</label>
            <Select
              options={departments}
              value={selectedDepartment}
              onChange={setSelectedDepartment}
              isClearable
            />
          </div>
          {items.length > 0 && (
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={globalFilter || ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
            </div>
          )}
          <div className="col-md-4 d-flex align-items-center">
            <button
              onClick={handleGetItems}
              disabled={loading}
              className="btn btn-outline-success me-2"
            >
              {loading ? 'Loading...' : 'Get Report'}
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={handleExportToExcel}
              disabled={items.length === 0} // Disable button if no items are loaded
            >
              Export to Excel
            </button>
          </div>
        </div>
      </div>
      <div className="table-responsive">
        <table
          style={{ fontSize: '15px', verticalAlign: 'baseline' }}
          className="table table-striped table-bordered"
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="d-flex justify-content-between align-items-center mt-3">

          <button
            className="btn btn-secondary me-2"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            Previous
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepartmentItem;
